import { useState, useCallback } from 'react';
import { Material, TemplateItem, ProductTemplate } from '../types';

export interface UseMaterialEditorProps {
  userId: string;
  materials: Material[];
  recipes?: ProductTemplate[];
  onSyncMaterials: (materials: Material[]) => void | Promise<void>;
}

export function useMaterialEditor({ userId, materials, recipes = [], onSyncMaterials }: UseMaterialEditorProps) {
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [editingMaterial, setEditingMaterial] = useState<Material | null>(null);
  const [pendingDeleteMaterial, setPendingDeleteMaterial] = useState<Material | null>(null);
  const [linkedRecipesForDelete, setLinkedRecipesForDelete] = useState<ProductTemplate[]>([]);

  const handleCreateNew = useCallback((): void => {
    setEditingMaterial({
      userId,
      name: '',
      category: 'variável',
      itemType: 'insumo',
      unit: 'un',
      pricePaid: 0,
      yieldAmount: 1,
      barcode: ''
    });
    setIsEditModalOpen(true);
  }, [userId]);

  const handleQuickAdd = useCallback((template: TemplateItem): void => {
    if (!template) return;
    setEditingMaterial({
      userId,
      name: template.nome_item || 'Item Sem Nome',
      category: (template.categoria || '').toLowerCase().includes('fixo') ? 'fixo' : 'variável',
      itemType: (template.categoria || '').toLowerCase().includes('fixo') ? 'custo_fixo' : 'insumo',
      unit: template.unidade || 'un',
      pricePaid: 0,
      yieldAmount: 1
    });
    setIsEditModalOpen(true);
  }, [userId]);

  const handleEditOpen = useCallback((material: Material): void => {
    if (!material) return;
    setEditingMaterial({ ...material });
    setIsEditModalOpen(true);
  }, []);

  const handleSaveMaterial = useCallback((): void => {
    if (!editingMaterial) return;

    const safeMaterials = Array.isArray(materials) ? materials : [];
    let updatedMaterials: Material[];

    if (editingMaterial.id) {
      updatedMaterials = safeMaterials.map(m => m.id === editingMaterial.id ? editingMaterial : m);
    } else {
      updatedMaterials = [...safeMaterials, { ...editingMaterial, id: crypto.randomUUID() }];
    }

    onSyncMaterials(updatedMaterials);
    setIsEditModalOpen(false);
    setEditingMaterial(null);
  }, [editingMaterial, materials, onSyncMaterials]);

  const confirmDeleteMaterial = useCallback((id: string): void => {
    const safeMaterials = Array.isArray(materials) ? materials : [];
    const updatedMaterials = safeMaterials.filter(m => m.id !== id);
    
    onSyncMaterials(updatedMaterials);
    setIsEditModalOpen(false);
    setEditingMaterial(null);
    setPendingDeleteMaterial(null);
    setLinkedRecipesForDelete([]);
  }, [materials, onSyncMaterials]);

  const handleDeleteMaterial = useCallback((id: string): void => {
    const targetMaterial = materials.find(m => m.id === id);
    const linked = recipes.filter(r => r.items?.some(i => i.materialId === id));

    if (linked.length > 0 && targetMaterial) {
      setPendingDeleteMaterial(targetMaterial);
      setLinkedRecipesForDelete(linked);
    } else {
      confirmDeleteMaterial(id);
    }
  }, [materials, recipes, confirmDeleteMaterial]);

  const cancelDeleteMaterial = useCallback((): void => {
    setPendingDeleteMaterial(null);
    setLinkedRecipesForDelete([]);
  }, []);

  return {
    isEditModalOpen,
    setIsEditModalOpen,
    editingMaterial,
    setEditingMaterial,
    pendingDeleteMaterial,
    linkedRecipesForDelete,
    handleCreateNew,
    handleQuickAdd,
    handleEditOpen,
    handleSaveMaterial,
    handleDeleteMaterial,
    confirmDeleteMaterial,
    cancelDeleteMaterial,
  };
}

